import type { ReactNode } from 'react';

import { ArrowLeft, Building2, FileText, History, Landmark, ShieldCheck } from 'lucide-react';

import { defaultSourceGroups } from '@addava/skills/source-presentation';
import { Skeleton } from '~/components/ui/skeleton';
import { Spinner } from '~/components/ui/spinner';
import { Trans } from '~/components/ui/trans';

/**
 * The page's shape before the address or any city record is verified. It
 * mirrors `PropertyDetails` while a lookup is pending so the swap to real
 * content does not change the layout: a full-width progress panel over the
 * body and rail columns.
 *
 * `preview` keeps the public lookup's "opening" status; `saved` mirrors a
 * saved property page (back link, map, tabs) so the workspace route can show
 * it the instant a property row is clicked.
 */
export function PropertyLoadingShell({
  opening,
  variant = 'preview',
}: { opening?: ReactNode; variant?: 'preview' | 'saved' } = {}) {
  const saved = variant === 'saved';
  return (
    <div
      className="pw-property-page pw-property-loading-shell"
      data-test="property-records-loading"
      aria-busy="true"
    >
      {saved && (
        <span className="pw-back-link" aria-hidden="true">
          <ArrowLeft className="size-4" />
          <Trans i18nKey="parcelWatch.workspace.properties" />
        </span>
      )}
      <div className="pw-property-layout">
        <header className="pw-property-heading">
          <div className="pw-property-title">
            <span className="pw-property-icon" aria-hidden="true">
              <Building2 className="size-7" />
            </span>
            <div className="pw-loading-identity">
              {saved ? (
                <Skeleton className="h-3 w-24" aria-hidden="true" />
              ) : (
                <p className="pw-eyebrow">
                  <Trans i18nKey="parcelWatch.property.preview" />
                </p>
              )}
              <h1 className="sr-only">
                <Trans i18nKey="parcelWatch.progress.openingTitle" />
              </h1>
              <Skeleton className="pw-loading-address" aria-hidden="true" />
              <Skeleton className="pw-loading-location" aria-hidden="true" />
            </div>
          </div>
        </header>
        {!saved && (
          <div className="pw-property-cta-top" aria-hidden="true">
            <MonitorCardSkeleton />
          </div>
        )}
        {(opening || !saved) && (
          <div className="pw-property-progress">{opening ?? <LookupProgressSkeleton />}</div>
        )}
        <aside className="pw-property-rail" aria-hidden="true">
          <MonitorCardSkeleton />
          <MapCardSkeleton />
          <MapCardSkeleton streetView />
          <div className="pw-neighbor-links pw-neighbor-placeholder">
            <Skeleton className="h-4 w-36" />
          </div>
        </aside>
        <div className="pw-property-body">
          <div className="pw-loading-tabs" aria-hidden="true">
            <span>
              <History className="size-4" />
              <Trans i18nKey="parcelWatch.property.timeline" />
            </span>
            <span>
              <ShieldCheck className="size-4" />
              <Trans
                i18nKey={
                  saved ? 'parcelWatch.property.compliance' : 'parcelWatch.property.actionItems'
                }
              />
            </span>
            <span>
              <Landmark className="size-4" />
              <Trans i18nKey="parcelWatch.property.publicFacts" />
            </span>
            {saved && (
              <span>
                <FileText className="size-4" />
                <Trans i18nKey="parcelWatch.property.documents" />
              </span>
            )}
          </div>
          <div className="pw-detail-main pt-5">
            <div className="pw-feed-filters" aria-hidden="true">
              <Skeleton className="h-7 w-14 rounded-full" />
              <Skeleton className="h-7 w-28 rounded-full" />
            </div>
            <PropertySectionSkeleton showLabel={false} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** The monitor call-to-action card's block shapes. */
function MonitorCardSkeleton() {
  return (
    <div className="pw-rail-card pw-loading-monitor">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="mt-4 h-3 w-full" />
      <Skeleton className="mt-2 h-3 w-4/5" />
      <Skeleton className="mt-5 h-9 w-full" />
      <Skeleton className="mt-3 h-9 w-full" />
      <Skeleton className="mt-3 h-3 w-3/5" />
    </div>
  );
}

/** The public lookup's "opening" card, laid out like the live progress panel. */
function LookupProgressSkeleton() {
  return (
    <section className="pw-lookup-progress" role="status">
      <div className="pw-lookup-progress-heading">
        <span className="pw-lookup-progress-icon" aria-hidden="true">
          <Spinner className="size-5" />
        </span>
        <div className="pw-lookup-progress-copy">
          <h2>
            <Trans i18nKey="parcelWatch.navigation.openingTitle" />
          </h2>
          <p>
            <Trans i18nKey="parcelWatch.navigation.openingDescription" />
          </p>
        </div>
        <Skeleton className="h-3 w-14 shrink-0" aria-hidden="true" />
      </div>
      <LookupSourceGroupsSkeleton />
      <div className="pw-lookup-progress-foot" aria-hidden="true">
        <Skeleton className="h-3 w-72" />
      </div>
      <div className="pw-lookup-source-details" aria-hidden="true">
        <Skeleton className="my-3 h-3.5 w-44" />
      </div>
    </section>
  );
}

/** The progress panel's source-group columns as placeholder blocks. */
export function LookupSourceGroupsSkeleton() {
  return (
    <ul className="pw-lookup-source-groups" aria-hidden="true">
      {defaultSourceGroups.map(({ key }) => (
        <li key={key}>
          <Skeleton className="pw-lookup-source-icon" />
          <div>
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-2 h-3 w-20" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** The rail's map or Street View card: a media viewport plus a caption row. */
function MapCardSkeleton({ streetView = false }: { streetView?: boolean }) {
  return (
    <div className="pw-map-card">
      {streetView ? (
        <Skeleton className="pw-street-view-lazy rounded-none" />
      ) : (
        <div className="pw-map-viewport">
          <Skeleton className="size-full rounded-none" />
        </div>
      )}
      <p className="pw-map-caption">
        <Skeleton className="h-3 w-40" />
      </p>
    </div>
  );
}

export function PropertySectionSkeleton({
  kind = 'records',
  showLabel = true,
}: {
  kind?: 'records' | 'facts';
  showLabel?: boolean;
}) {
  return (
    <section className="pw-section-loading" aria-busy="true" data-test={`property-${kind}-pending`}>
      {showLabel && (
        <p className="pw-section-loading-label">
          <span className="pw-lookup-source-dot" aria-hidden="true" />
          <Trans i18nKey={`parcelWatch.progress.${kind}Loading`} />
        </p>
      )}
      {kind === 'facts' ? (
        <div className="pw-loading-facts" aria-hidden="true">
          {[0, 1, 2, 3].map((key) => (
            <div key={key}>
              <Skeleton className="h-3 w-20" />
              <Skeleton className="mt-4 h-8 w-16" />
            </div>
          ))}
        </div>
      ) : (
        <div aria-hidden="true">
          {[0, 1, 2].map((key) => (
            <div key={key} className="pw-loading-record-row">
              <Skeleton className="size-9 shrink-0 rounded-lg" />
              <div>
                <Skeleton className={key === 1 ? 'h-4 w-3/5' : 'h-4 w-4/5'} />
                <Skeleton className="mt-3 h-3 w-1/2" />
              </div>
              <Skeleton className="h-6 w-16 shrink-0" />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
